import type { ITeamMember, IUser } from "~/types";
import type { ITeam } from "@/teams/types/teams";

const PAGE_WITHOUT_ROLES_OR_PERMISSIONS = ["athletes", "leagues", "user", "users"/** Only available for SA */];

export interface IPendoContext {
  [key: string]: any;
}

class MetadataController {
  public user: IUser;

  constructor(user: IUser) {
    this.user = user;
  }

  async getUserTeamsMetadata(team_id?: string): Promise<IPendoContext["teams"]> {
    try {
      if (team_id) {
        const team = await getCachedUserTeamById(team_id);
        return { selected_team: [team_id, team?.name ?? "", team?.user_role] };
      }

      const teams = { data: [] as ITeam[], total: 0 };
      let res;

      do {
        res = await getCachedUserTeamsList({
          relationships: "memberships,organizations",
          limit: 100,
          offset: teams.data.length,
        });
        teams.data.push(...res.data);
        teams.total = res.total;
      } while (teams.total > teams.data.length && res.data.length > 0);

      return {
        ...teams.data.reduce((acc: Record<string, string[]>, t) => {
          acc[`team_${t.user_role}`] = [...(acc[`team_${t.user_role}`] || []), t.id];
          return acc;
        }, {}),
      };
    }
    catch (error) {
      console.warn("Failed to get user permissions in team page:", team_id, error);
      return;
    }
  }

  async getUserOrganizationsMetadata(organization_id?: string): Promise<IPendoContext["organizations"]> {
    if (!organization_id) return;

    try {
      const organization = await getCachedOrganizationById(organization_id);
      const members = await getOrganizationMembersById(organization_id);
      const member = members.data?.find((m: ITeamMember) => m.atlitUserId === this.user?.atlitId);
      return { selected_organization: [organization_id, organization?.name || "", member?.user_role || "not_member", organization?.organization_type] };
    }
    catch (error) {
      console.warn("Failed to get user permissions in organization page:", organization_id, error);
      return;
    }
  }

  async getUserEventsMetadata(eventId?: string): Promise<IPendoContext["events"]> {
    if (!eventId) return;
    try {
      const event = await getCachedEventById(eventId);
      return { selected_event: [event?.id, event?.title, event?.status, event?.breakdowns?.status, event.sport_type, event.game_info?.type, event?.is_favorite && "favorite"].filter(Boolean) };
    }
    catch (error) {
      console.warn("Failed to get user permissions in event page:", eventId, error);
      return;
    }
  }

  async getUserAccountMetadata() {
    try {
      const teams = await getCachedUserTeamsList({
        relationships: "breakdowns,memberships,organizations",
        roles: "owner,admin",
        limit: 20,
        offset: 0,
      });

      return { has_breakdowns_page_access: !!teams?.data?.length };
    }
    catch (error) {
      console.warn("Failed to get user permissions in account page", error);
      return;
    }
  }

  async getMetadataForContext(type: string, id?: string): Promise<IPendoContext[keyof IPendoContext]> {
    switch (type) {
      case "teams":
        return await this.getUserTeamsMetadata(id);
      case "organizations":
        return await this.getUserOrganizationsMetadata(id);
      case "events":
        return await this.getUserEventsMetadata(id);
      case "account":
        return await this.getUserAccountMetadata();
      default:
        return;
    }
  }
}

// Create singleton memoized functions to ensure caching works globally
const getCurrentUserData = useMemoize(async () => {
  const { $i18n } = useNuxtApp();
  const app_language = $i18n?.locale?.value || "en";
  const user = useUser();
  // Wait for user data to be available
  await until(() => user.value?.atlitId).toBeTruthy();

  return {
    id: user.value?.atlitId || "",
    email: user.value?.email || "",
    full_name: user.value?.fullName || "",
    // Additional useful metadata
    has_super_admin_role: Boolean(user.value?.hasSuperAdminRole),
    has_b2b_account: Boolean(user.value?.hasB2BAccount),
    has_tenant_admin_role: Boolean(user.value?.hasTenantAdminRole),
    app_language,
  };
});

const getCurrentContext = useMemoize(async (): Promise<IPendoContext | null> => {
  const route = useRoute();

  const segments = route.path.split("/").filter(Boolean);
  const name = segments[0];
  const id = Object.entries(route.params)?.find(([key]) => key.includes("Id"))?.[1];

  if (!name || PAGE_WITHOUT_ROLES_OR_PERMISSIONS.includes(name)) return null;

  const context: IPendoContext = {};
  const user = useUser();

  if (user.value?.hasSuperAdminRole) {
    return {};
  }

  const metadataController = new MetadataController(user.value!);
  const metadata = await metadataController.getMetadataForContext(name, id as string);

  if (Object.keys(metadata ?? {}).length) {
    Object.assign(context, metadata);
  }

  return context;
});

const getPendoMetadata = useMemoize(async () => {
  const userData = await getCurrentUserData();
  const context = await getCurrentContext();

  return {
    ...userData,
    ...context,
  };
});

export const usePendo = () => {
  const clearPendoDataCache = (): void => {
    getCurrentUserData.clear();
    getCurrentContext.clear();
    getPendoMetadata.clear();
  };

  return {
    getCurrentUserData,
    getCurrentContext,
    getPendoMetadata,
    clearPendoDataCache,
  };
};
